import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Star, TrendingUp, Package, Lock, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { ordersFromSpend, formatINR } from '../../lib/dropcalc';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_RANK = { Basic: 1, Advanced: 2, Professional: 3 };
const REF_SPEND = 1000; // reference daily ad spend for auto-calculated order estimate

// Step 2 - Product selection. ALL products shown in one grid.
// Products above the chosen store tier are LOCKED.
const ProductSelection = ({ storeType, selectedProducts, onProductsChange, onNext, onBack }) => {
  const maxProducts = storeType?.maxProducts || 1;
  const allowed = storeType?.allowedCategories || ['low'];
  const [products, setProducts] = useState([]);
  const [localSelected, setLocalSelected] = useState(selectedProducts || []);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/products`);
        if (!cancelled && Array.isArray(res.data)) {
          // Defensive: dedupe by id so duplicate React keys can never break clicks
          const seen = new Set();
          const unique = res.data.filter((p) => {
            if (!p || seen.has(p.id)) return false;
            seen.add(p.id);
            return true;
          });
          setProducts(unique);
        }
      } catch (_e) { /* ignore */ } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Drop any previously-selected products that are no longer unlocked for this tier
  useEffect(() => {
    setLocalSelected((prev) => prev.filter((p) => allowed.includes(p.category)).slice(0, maxProducts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeType?.name]);

  const isUnlocked = (product) => allowed.includes(product.category);

  const toggleProduct = (product) => {
    if (!isUnlocked(product)) return;
    setLocalSelected((prev) => {
      if (prev.find((p) => p.id === product.id)) return prev.filter((p) => p.id !== product.id);
      if (prev.length >= maxProducts) return prev;
      return [...prev, product];
    });
  };

  const handleContinue = () => {
    if (localSelected.length > 0) { onProductsChange(localSelected); onNext(); }
  };

  const unlockLabel = (tier) => `Unlock with ${tier} store`;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-200 rounded-full mb-3">
          <Package className="w-4 h-4 mr-2 text-blue-600" />
          <span className="text-blue-700 font-medium text-sm">{storeType?.name} Store — pick up to {maxProducts} product{maxProducts > 1 ? 's' : ''}</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-slate-800 mb-1">Choose your Winning Products</h2>
        <p className="text-slate-500 text-sm">Selected: <span className="text-blue-600 font-bold">{localSelected.length}</span> / {maxProducts}</p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-500">
          <div className="w-10 h-10 border-4 border-blue-500/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />Loading products...
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 mb-8">
          {products.map((product) => {
            const isSelected = !!localSelected.find((p) => p.id === product.id);
            const unlocked = isUnlocked(product);
            const atLimit = !isSelected && localSelected.length >= maxProducts;
            const estOrders = ordersFromSpend(REF_SPEND, product.avgCPP);
            return (
              <Card key={product.id}
                className={`relative transition-all duration-300 overflow-hidden ${
                  !unlocked ? 'opacity-95 border border-slate-200 bg-slate-50 cursor-not-allowed'
                    : isSelected ? 'bg-blue-50 border-2 border-blue-500 shadow-lg shadow-blue-500/20 cursor-pointer'
                    : atLimit ? 'bg-white border border-slate-200 opacity-60 cursor-not-allowed'
                    : 'bg-white border border-slate-200 hover:border-blue-400 hover:shadow-lg cursor-pointer'}`}
                onClick={() => unlocked && !atLimit && toggleProduct(product)}>

                {/* Tier badge */}
                <div className={`absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  product.tier === 'Basic' ? 'bg-blue-100 text-blue-700' : product.tier === 'Advanced' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-amber-100 text-amber-700'}`}>{product.tier}</div>

                {isSelected && (
                  <div className="absolute top-2 right-2 z-10 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center"><Check className="w-4 h-4 text-white" /></div>
                )}
                {product.recommended && unlocked && (
                  <div className="absolute bottom-2 left-2 z-10 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 text-white" fill="white" /><span className="text-[10px] text-white font-bold">Top</span>
                  </div>
                )}

                <CardContent className="p-3">
                  <div className="aspect-square relative rounded-lg overflow-hidden mb-2 bg-white">
                    <img src={product.image} alt={product.name} className={`w-full h-full object-contain p-2 ${!unlocked ? 'blur-[2px] grayscale' : ''}`}
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/300?text=' + encodeURIComponent(product.name); }} />
                    {!unlocked && (
                      <div className="absolute inset-0 bg-slate-900/40 flex flex-col items-center justify-center text-center px-2">
                        <Lock className="w-6 h-6 text-white mb-1" />
                        <span className="text-[10px] font-bold text-white leading-tight">{unlockLabel(product.tier)}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-slate-800 font-semibold text-xs sm:text-sm mb-2 line-clamp-2 min-h-[2.2em]">{product.name}</h3>
                  <div className="space-y-1 text-[11px]">
                    <div className="flex justify-between"><span className="text-slate-500">Cost</span><span className="text-slate-700">{formatINR(product.productCost)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Sell</span><span className="text-slate-900 font-semibold">{formatINR(product.sellingPrice)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Profit</span><span className="text-green-600 font-semibold">{formatINR(product.profit)}</span></div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-slate-200 flex items-center justify-between">
                    <span className="text-[11px] text-blue-600 font-medium">CPP {formatINR(0).replace('0','')}{product.avgCPP}</span>
                    <span className="inline-flex items-center gap-1 text-[11px] text-green-600 font-semibold"><TrendingUp className="w-3 h-3" />~{estOrders}/{formatINR(REF_SPEND)}</span>
                  </div>
                  <p className="text-[9px] text-slate-400 mt-1 text-right leading-none">est. orders per day ad-spend</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {localSelected.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
          <h3 className="text-slate-800 font-semibold mb-2 text-sm">Selected:</h3>
          <div className="flex flex-wrap gap-2">
            {localSelected.map((p) => (
              <div key={p.id} className="flex items-center gap-2 bg-white border border-blue-200 rounded-lg px-2 py-1 shadow-sm">
                <img src={p.image} alt={p.name} className="w-7 h-7 rounded object-cover bg-white" />
                <span className="text-xs text-slate-800">{p.name}</span>
                <button onClick={(e) => { e.stopPropagation(); toggleProduct(p); }} className="text-slate-400 hover:text-red-500">×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-slate-500"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        <Button onClick={handleContinue} disabled={localSelected.length === 0} data-testid="products-next"
          className="bg-blue-600 hover:bg-blue-700 text-white px-8 disabled:bg-slate-300">Continue <ArrowRight className="w-4 h-4 ml-2" /></Button>
      </div>
    </div>
  );
};

export default ProductSelection;
